import { BaseClient } from '../base-client';
import type {
  CreateProductInput,
  PatchProductInput,
  Product,
  SetProductStatusInput,
} from '../products/types';

type One = { data: Product };
type Many = { data: Product[] };

/**
 * Customer-specific activities — the per-customer view over the activity
 * catalog. Drives the customer-detail "Activities" tab. Every call is nested
 * under a customer (`/api/customers/:customerId/activities`) and the server
 * requires BOTH `customer.*` and `activity.*` permissions plus the caller's
 * customer scope. A custom activity is an ordinary {@link Product} whose
 * `customerId` is the owning customer.
 */
export class CustomerActivitiesClient extends BaseClient {
  /** Every activity defined for this customer (all statuses, name-sorted). */
  list(customerId: string): Promise<Many> {
    return this.request(`/api/customers/${encodeURIComponent(customerId)}/activities`);
  }

  get(customerId: string, id: string): Promise<One> {
    return this.request(
      `/api/customers/${encodeURIComponent(customerId)}/activities/${encodeURIComponent(id)}`,
    );
  }

  create(customerId: string, input: CreateProductInput): Promise<One> {
    return this.request(`/api/customers/${encodeURIComponent(customerId)}/activities`, {
      method: 'POST',
      body: input,
    });
  }

  patch(customerId: string, id: string, input: PatchProductInput): Promise<One> {
    return this.request(
      `/api/customers/${encodeURIComponent(customerId)}/activities/${encodeURIComponent(id)}`,
      { method: 'PATCH', body: input },
    );
  }

  setStatus(customerId: string, id: string, input: SetProductStatusInput): Promise<One> {
    return this.request(
      `/api/customers/${encodeURIComponent(customerId)}/activities/${encodeURIComponent(id)}/status`,
      { method: 'POST', body: input },
    );
  }

  /** Soft delete — flips the activity's status to Inactive. */
  delete(customerId: string, id: string): Promise<One> {
    return this.request(
      `/api/customers/${encodeURIComponent(customerId)}/activities/${encodeURIComponent(id)}`,
      { method: 'DELETE', body: {} },
    );
  }
}
